import { useState, useEffect } from 'react';
import { useLoading } from '../../../../hooks/useLoading';
import { useNotifications } from '../../../../hooks/useNotifications';
import {
  getApplicationByIdRequest,
  getEngagementByIdRequest,
  getRCMByIdRequest,
  getGapIdFromSOC2EngagementId,
  getServiceById
} from '../../../../constants/requests';

/**
 * Custom hook for managing Testing component data
 * Handles fetching RCM data, engagement data, and gap tracker data
 *
 * @param {string} applicationId - The application ID
 * @param {string} engagementId - The engagement ID
 * @returns {Object} - Data and state management for testing
 */
export const useTestingData = (applicationId, engagementId) => {
  const { showNotifications } = useNotifications();
  const { request } = useLoading();

  const [applicationName, setApplicationName] = useState('null');
  const [RCMData, setRCMData] = useState(null);
  const [RCMId, setRCMId] = useState(null);
  const [type, setType] = useState(null);
  const [socScope, setSocScope] = useState(null);
  const [gapTracker, setGapTracker] = useState(null);
  const [testingSheetType, setTestingSheetType] = useState(null);
  const [dynamicTestingTabObject, setDynamicTestingTabObject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const getRCM = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let response = await request(() =>
        getApplicationByIdRequest(applicationId)
      );

      if (response.data.statusCode === 200) {
        const RCMId = response.data.body.RCMId?._id;
        setApplicationName(response.data.body.applicationName);

        if (!RCMId) {
          // RCM not created yet
          setRCMData(null);
          setIsLoading(false);
          return;
        }

        response = await request(() => getRCMByIdRequest(RCMId));

        // Set dynamic testing tab object if TestingSheetTab exists
        if (response.data.body.data.meta.TestingSheetTab) {
          setDynamicTestingTabObject({
            'Test Procedure': response.data.body.data.meta.TestingSheetTab
          });
        }

        setTestingSheetType(response.data.body.testingSheetType);
        setRCMData(response.data.body.data);
        setRCMId(response.data.body._id);
        setType(response.data.body.templateType);

        response = await request(() => getEngagementByIdRequest(engagementId));

        const res = await request(() =>
          getServiceById(response.data.body.serviceId)
        );

        if (res.data.body.name === 'SOC2.0') {
          setSocScope(response.data.body.understandingId.socScope);
          const gapTrackerResponse = await request(() =>
            getGapIdFromSOC2EngagementId(engagementId)
          );
          setGapTracker(gapTrackerResponse.data.body);
        }
      } else {
        setError(response.data.message);
        showNotifications('error', response.data.message, 5000);
      }
    } catch (err) {
      setError(err.toString());
      showNotifications('error', err.toString(), 5000);
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = () => {
    getRCM();
  };

  useEffect(() => {
    getRCM();
  }, []);

  return {
    RCMData,
    setRCMData,
    RCMId,
    type,
    socScope,
    gapTracker,
    applicationName,
    testingSheetType,
    dynamicTestingTabObject,
    isLoading,
    error,
    refreshData
  };
};
